import React, { useState } from 'react';

import { SURFACE, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, Button, ButtonBase, TextField, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatRelativeTime } from '~/pages/AskRichardUnified/chat/SessionHistoryPanel';

import {
  type LensCertificationRequest,
  useActOnLensCertification,
  useGetLensCertificationRequests,
} from '~/hooks/apis/reporting/useLensCertification';
import { useGetBrowsableLensSpaces } from '~/hooks/apis/reporting/useLensSpaces';
import useCurrentUser from '~/hooks/useCurrentUser';

import { isOwnLens, ownerFirstName } from '../lensLibrary';

const requestedBy = (entry: LensCertificationRequest): { by: string; at: string } | null => {
  const { badge, request } = entry.certification;
  if (badge.kind === 'changed') return { at: badge.at, by: badge.by };
  return request.kind === 'open' ? { at: request.at, by: request.by } : null;
};

function QueueRow({
  entry,
  onOpenLens,
  viewerEmail,
}: {
  entry: LensCertificationRequest;
  onOpenLens: (lensId: string) => void;
  viewerEmail?: string | null;
}) {
  const tokens = lensTokens(useTheme());
  const act = useActOnLensCertification();
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const { certification, lens } = entry;
  const asked = requestedBy(entry);
  const home =
    entry.home.kind === 'space'
      ? entry.home.spaceName
      : isOwnLens(lens, viewerEmail)
        ? 'My Lenses'
        : `${ownerFirstName(lens.ownerEmail)}’s Lenses`;
  const canDecline = certification.request.kind === 'open';
  return (
    <Box
      sx={{
        bgcolor: tokens.surface,
        border: `1px solid ${alpha(tokens.accent, SURFACE.hairlineAlpha)}`,
        borderRadius: `${SURFACE.radius}px`,
        mt: '6px',
        px: '12px',
        py: '8px',
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'grid', gap: '12px', gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
        <Box sx={{ minWidth: 0 }}>
          <ButtonBase
            onClick={() => onOpenLens(lens.id)}
            sx={{
              color: tokens.accent,
              display: 'block',
              fontSize: 13.5,
              fontWeight: 600,
              maxWidth: '100%',
              overflow: 'hidden',
              textAlign: 'left',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {lens.title}
          </ButtonBase>
          <Typography color="text.secondary" noWrap sx={{ fontSize: 12.5 }}>
            {[
              home,
              asked ? `requested by ${ownerFirstName(asked.by)}` : 'not certified',
              asked ? formatRelativeTime(asked.at, Date.now()) : '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canDecline && (
            <Button disabled={act.isPending} onClick={() => setDeclining((current) => !current)} size="small">
              Decline
            </Button>
          )}
          <Button
            disabled={act.isPending || lens.spec.cards.length === 0}
            onClick={() => act.mutate({ action: 'certify', lensId: lens.id, meaning: certification.meaning })}
            size="small"
            sx={{
              bgcolor: tokens.accent,
              boxShadow: 'none',
              color: tokens.accentContrast,
              '&:hover': { bgcolor: alpha(tokens.accent, 0.92), boxShadow: 'none' },
            }}
            variant="contained"
          >
            Certify
          </Button>
        </Box>
      </Box>
      {declining && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            onChange={(event) => setNote(event.target.value)}
            placeholder="Why it is declined"
            size="small"
            value={note}
          />
          <Button
            disabled={act.isPending || !note.trim()}
            onClick={() => act.mutate({ action: 'decline', lensId: lens.id, note: note.trim() })}
            size="small"
          >
            Send
          </Button>
        </Box>
      )}
    </Box>
  );
}

/** Requests with an open ask. A Lens nobody has asked to certify is not a request. */
export const openCertificationRequests = (requests: Array<LensCertificationRequest>) =>
  requests.filter((entry) => entry.certification.request.kind === 'open');

/**
 * The reviewers' queue: each Lens with an open certification request, the people and time behind
 * it, and Certify or Decline. Renders nothing for anyone who cannot review.
 */
export default function LensCertificationQueue({ onOpenLens }: { onOpenLens: (lensId: string) => void }) {
  const { user } = useCurrentUser();
  const browsable = useGetBrowsableLensSpaces();
  const canReview = Boolean(browsable.data?.libraryAccess.canReviewCertification);
  const requests = useGetLensCertificationRequests(canReview);
  if (!canReview) {
    return null;
  }
  if (requests.isLoading) {
    return null;
  }
  if (requests.isError) {
    return (
      <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
        Couldn’t load requests.{' '}
        <ButtonBase
          onClick={() => void requests.refetch()}
          sx={{ color: 'inherit', fontSize: 'inherit', textDecoration: 'underline', verticalAlign: 'baseline' }}
        >
          Retry
        </ButtonBase>
      </Typography>
    );
  }
  const queue = openCertificationRequests(requests.data ?? []);
  if (queue.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
        No requests waiting.
      </Typography>
    );
  }
  return (
    <Box>
      {queue.map((entry) => (
        <QueueRow entry={entry} key={entry.lens.id} onOpenLens={onOpenLens} viewerEmail={user?.email} />
      ))}
    </Box>
  );
}
